/**
 * ACCESSIBILITY ENHANCEMENTS - WCAG 2.2 AA Compliance JavaScript
 * Provides enhanced keyboard navigation, focus management, and screen reader support
 */

class AccessibilityEnhancer {
  constructor() {
    this.focusableElements = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]:not([contenteditable="false"])',
    ].join(',');

    this.init();
  }

  init() {
    this.setupKeyboardNavigation();
    this.setupFocusManagement();
    this.setupScreenReaderAnnouncements();
    this.setupModalAccessibility();
    this.setupSkipLinks();
    this.setupFormAccessibility();
    this.setupCartAccessibility();
    this.setupSearchAccessibility();
    this.preventTabTrap();
  }

  // Enhanced Keyboard Navigation
  setupKeyboardNavigation() {
    document.addEventListener('keydown', (event) => {
      // Escape key handling
      if (event.key === 'Escape') {
        this.handleEscapeKey();
      }

      // Enter key on buttons/links
      if (event.key === 'Enter' || event.key === ' ') {
        this.handleActivation(event);
      }

      // Arrow key navigation for menus
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        this.handleArrowNavigation(event);
      }
    });
  }

  handleEscapeKey() {
    // Close any open modals
    const openModal = document.querySelector('.modal[aria-hidden="false"]');
    if (openModal) {
      this.closeModal(openModal);
      return;
    }

    // Close any open drawers
    const openDrawer = document.querySelector('.drawer[aria-hidden="false"]');
    if (openDrawer) {
      this.closeDrawer(openDrawer);
      return;
    }

    // Close search if open
    const searchForm = document.querySelector('.search-modal[aria-hidden="false"]');
    if (searchForm) {
      this.closeSearch();
    }
  }

  handleActivation(event) {
    const target = event.target;

    if (target.tagName === 'A' && target.getAttribute('href') === '#') {
      event.preventDefault();
    }

    // Handle custom button elements
    if (target.hasAttribute('role') && target.getAttribute('role') === 'button') {
      event.preventDefault();
      target.click();
    }
  }

  handleArrowNavigation(event) {
    const currentElement = event.target;
    const menu =
      currentElement.closest('[role="menu"]') ||
      currentElement.closest('.menu') ||
      currentElement.closest('.list-menu');

    if (!menu) return;

    const menuItems = Array.from(menu.querySelectorAll(this.focusableElements)).filter((el) => this.isVisible(el));

    if (menuItems.length === 0) return;

    const currentIndex = menuItems.indexOf(currentElement);
    let nextIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        nextIndex = (currentIndex + 1) % menuItems.length;
        menuItems[nextIndex].focus();
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        nextIndex = (currentIndex - 1 + menuItems.length) % menuItems.length;
        menuItems[nextIndex].focus();
        break;
    }
  }

  // Focus Management
  setupFocusManagement() {
    // Focus trap for modals
    this.setupFocusTrap();

    // Return focus management
    this.setupReturnFocus();

    // Focus visible indicators
    this.setupFocusVisible();
  }

  setupFocusTrap() {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      const modal = document.querySelector('.modal[aria-hidden="false"]');
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(this.focusableElements);
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    });
  }

  setupReturnFocus() {
    this.lastFocusedElement = null;

    // Store focus before opening modals/drawers
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-modal-trigger]') || event.target.closest('[data-drawer-trigger]');

      if (trigger) {
        this.lastFocusedElement = trigger;
      }
    });
  }

  setupFocusVisible() {
    // Add focus-visible polyfill behavior
    document.addEventListener('keydown', () => {
      document.body.classList.add('using-keyboard');
    });

    document.addEventListener('mousedown', () => {
      document.body.classList.remove('using-keyboard');
    });
  }

  // Screen Reader Announcements
  setupScreenReaderAnnouncements() {
    this.liveRegion = document.getElementById('accessibility-live-region');
    this.assertiveRegion = document.getElementById('accessibility-live-region-assertive');

    if (!this.liveRegion) {
      this.createLiveRegions();
    }

    // Cart update announcements
    this.setupCartAnnouncements();

    // Search announcements
    this.setupSearchAnnouncements();

    // Form validation announcements
    this.setupFormAnnouncements();
  }

  createLiveRegions() {
    // Polite live region
    this.liveRegion = document.createElement('div');
    this.liveRegion.id = 'accessibility-live-region';
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.setAttribute('role', 'status');
    this.liveRegion.className = 'sr-only';
    document.body.appendChild(this.liveRegion);

    // Assertive live region
    this.assertiveRegion = document.createElement('div');
    this.assertiveRegion.id = 'accessibility-live-region-assertive';
    this.assertiveRegion.setAttribute('aria-live', 'assertive');
    this.assertiveRegion.setAttribute('aria-atomic', 'true');
    this.assertiveRegion.setAttribute('role', 'alert');
    this.assertiveRegion.className = 'sr-only';
    document.body.appendChild(this.assertiveRegion);
  }

  announce(message, priority = 'polite') {
    const region = priority === 'assertive' ? this.assertiveRegion : this.liveRegion;

    if (region) {
      region.textContent = '';
      setTimeout(() => {
        region.textContent = message;
      }, 100);
    }
  }

  setupCartAnnouncements() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      const { item, quantity } = event.detail;
      let message;

      if (quantity > 0) {
        message = `Added ${item.product_title} to cart. Cart now has ${quantity} items.`;
      } else {
        message = `Removed ${item.product_title} from cart.`;
      }

      this.announce(message);
    });

    // Cart errors
    document.addEventListener('cart:error', (event) => {
      const { error } = event.detail;
      this.announce(`Cart error: ${error}`, 'assertive');
    });
  }

  setupSearchAnnouncements() {
    const searchInput = document.querySelector('.search__input');

    if (searchInput) {
      let searchTimeout;

      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          const query = searchInput.value.trim();
          if (query.length > 2) {
            const resultsContainer = document.querySelector('.search__results');
            if (resultsContainer) {
              const resultCount = resultsContainer.querySelectorAll('.search__result').length;
              this.announce(`${resultCount} search results found for "${query}"`);
            }
          }
        }, 500);
      });
    }
  }

  setupFormAnnouncements() {
    // Form validation announcements
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const errors = form.querySelectorAll('.field__error:not(:empty)');

      if (errors.length > 0) {
        event.preventDefault();
        this.announce(
          `Form has ${errors.length} error${errors.length > 1 ? 's' : ''}. Please check and correct.`,
          'assertive',
        );

        // Focus first error
        const firstErrorField = errors[0].closest('.field').querySelector('.field__input');
        if (firstErrorField) {
          firstErrorField.focus();
        }
      }
    });
  }

  // Modal Accessibility
  setupModalAccessibility() {
    document.addEventListener('click', (event) => {
      const modalTrigger = event.target.closest('[data-modal-trigger]');
      const modalClose = event.target.closest('[data-modal-close]');

      if (modalTrigger) {
        const modalId = modalTrigger.getAttribute('data-modal-trigger');
        this.openModal(modalId);
      }

      if (modalClose) {
        const modal = modalClose.closest('.modal');
        this.closeModal(modal);
      }
    });

    // Close modal on overlay click
    document.addEventListener('click', (event) => {
      if (event.target.matches('.modal__overlay')) {
        const modal = event.target.closest('.modal');
        this.closeModal(modal);
      }
    });
  }

  openModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    this.lastFocusedElement = document.activeElement;

    modal.setAttribute('aria-hidden', 'false');
    modal.style.display = 'flex';
    document.body.classList.add('modal-open');

    // Focus first focusable element
    const firstFocusable = modal.querySelector(this.focusableElements);
    if (firstFocusable) {
      firstFocusable.focus();
    }

    this.announce('Modal opened');
  }

  closeModal(modal) {
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');
    modal.style.display = 'none';
    document.body.classList.remove('modal-open');

    // Return focus
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
    }

    this.announce('Modal closed');
  }

  // Skip Links
  setupSkipLinks() {
    const skipLinks = document.querySelectorAll('.skip-link');

    skipLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const target = document.getElementById(targetId);

        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // Form Accessibility
  setupFormAccessibility() {
    // Auto-announce form errors
    const forms = document.querySelectorAll('form');

    forms.forEach((form) => {
      const inputs = form.querySelectorAll('input, textarea, select');

      inputs.forEach((input) => {
        input.addEventListener('invalid', () => {
          setTimeout(() => {
            const errorElement = input.parentNode.querySelector('.field__error');
            if (errorElement) {
              this.announce(errorElement.textContent, 'assertive');
            }
          }, 100);
        });
      });
    });
  }

  // Cart Accessibility
  setupCartAccessibility() {
    // Quantity button announcements
    const quantityButtons = document.querySelectorAll('[data-quantity-button]');

    quantityButtons.forEach((button) => {
      button.addEventListener('click', () => {
        setTimeout(() => {
          const input = button.parentNode.querySelector('.quantity__input');
          if (input) {
            this.announce(`Quantity changed to ${input.value}`);
          }
        }, 100);
      });
    });
  }

  // Search Accessibility
  setupSearchAccessibility() {
    const searchToggle = document.querySelector('.search-toggle');
    const searchModal = document.querySelector('.search-modal');

    if (searchToggle && searchModal) {
      searchToggle.addEventListener('click', () => {
        const isOpen = searchModal.getAttribute('aria-hidden') === 'false';

        if (!isOpen) {
          this.openSearch();
        } else {
          this.closeSearch();
        }
      });
    }
  }

  openSearch() {
    const searchModal = document.querySelector('.search-modal');
    const searchInput = document.querySelector('.search__input');

    if (searchModal) {
      this.lastFocusedElement = document.activeElement;
      searchModal.setAttribute('aria-hidden', 'false');

      if (searchInput) {
        searchInput.focus();
      }

      this.announce('Search opened');
    }
  }

  closeSearch() {
    const searchModal = document.querySelector('.search-modal');

    if (searchModal) {
      searchModal.setAttribute('aria-hidden', 'true');

      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }

      this.announce('Search closed');
    }
  }

  // Prevent Tab Trapping
  preventTabTrap() {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(document.querySelectorAll(this.focusableElements)).filter(
        (el) => this.isVisible(el) && !this.isInert(el),
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    });
  }

  // Utility Methods
  isVisible(element) {
    const style = window.getComputedStyle(element);
    return (
      style.display !== 'none' && style.visibility !== 'hidden' && element.offsetWidth > 0 && element.offsetHeight > 0
    );
  }

  isInert(element) {
    let parent = element;
    while (parent) {
      if (parent.hasAttribute && (parent.hasAttribute('inert') || parent.getAttribute('aria-hidden') === 'true')) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  closeDrawer(drawer) {
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'true');
      drawer.classList.remove('drawer--active');

      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
      }

      this.announce('Menu closed');
    }
  }
}

// Initialize accessibility enhancements when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccessibilityEnhancer();
  });
} else {
  new AccessibilityEnhancer();
}

// Export for use in other scripts
window.AccessibilityEnhancer = AccessibilityEnhancer;
